import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/ui/VideoCard";
import Pagination from "@/components/ui/Pagination";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  TYPE_META,
  filterVideos,
  isVideoType,
  splitIntoPages,
  totalPages,
  type SortKey,
} from "@/lib/videos";
import type { Region } from "@/types/video";

const PAGE_SIZE = 24;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const validType = isVideoType(type);

  const meta = validType ? TYPE_META[type] : null;

  usePageMeta({
    title: meta
      ? `${meta.title} - 密桃视频 - 高清视频内容平台`
      : "分类不存在 - 密桃视频",
    description: meta
      ? `${meta.title}汇集各类${meta.label}内容，${meta.description}`
      : "抱歉，该分类不存在。",
    keywords: meta ? `${meta.label},${meta.title},密桃视频,高清视频` : "密桃视频",
  });

  const [region, setRegion] = useState<Region | "不限">("不限");
  const [year, setYear] = useState<number | "不限">("不限");
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const results = useMemo(() => {
    if (!validType) return [];
    return filterVideos({ type, region, year, sort });
  }, [validType, type, region, year, sort]);

  const pages = totalPages(results.length, PAGE_SIZE);
  const currentPage = Math.min(page, pages);
  const visible = splitIntoPages(results, currentPage, PAGE_SIZE);

  const resetFilters = () => {
    setRegion("不限");
    setYear("不限");
    setSort("hot");
    setPage(1);
  };

  const applyFilter = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  if (!validType || !meta) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
          该分类不存在
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          抱歉，你访问的视频分类可能已经调整或不存在。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-6 md:px-6 lg:px-8 md:py-8">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{meta.label}</span>
      </nav>

      <div className="relative mb-6 overflow-hidden rounded-lg border border-background-200/70 bg-background-100/70 p-6 md:p-8">
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${meta.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {meta.title}
              </h1>
              <p className="text-sm text-primary-600">{meta.slogan}</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700">{meta.description}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="w-full lg:w-[260px] lg:shrink-0">
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className="flex w-full items-center justify-between rounded-md border border-background-200/80 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800"
            >
              <span className="inline-flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500"></i>
                筛选条件
              </span>
              <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
          </div>

          <div
            className={`${filterOpen ? "mt-3 block" : "hidden"} rounded-lg border border-background-200/70 bg-background-50 p-5 lg:mt-0 lg:block lg:sticky lg:top-20`}
          >
            <FilterPanel
              region={region}
              year={year}
              sort={sort}
              onRegionChange={applyFilter(setRegion)}
              onYearChange={applyFilter(setYear)}
              onSortChange={applyFilter(setSort)}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-600">{results.length}</span> 部{meta.label}内容
            </p>
            <Link
              to="/search"
              className="inline-flex items-center gap-1 text-sm text-foreground-600 transition-colors hover:text-primary-600 whitespace-nowrap"
            >
              <i className="ri-search-line"></i>
              换个方式搜索
            </Link>
          </div>

          {visible.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-50 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <p className="mt-5 text-base font-medium text-foreground-800">
                抱歉，没有找到符合条件的视频
              </p>
              <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后再看看</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {visible.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination current={currentPage} total={pages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}